/**
 * Story 5.2 — de vastgestelde schermteksten bij de soortkeuze (§10.1 en §10.2).
 *
 * Alle zinnen staan hier op ÉÉN plek en niet verspreid door de JSX, zodat KE-7
 * met één blik na te lopen is: geen enkele regel hieronder is onderweg bedacht.
 * Dezelfde vorm als `HR_BEOORDEELT_ZELF_TEKSTEN` in `LeaveRequestSheet.tsx`.
 *
 * De sleutels zijn de soorten uit `VERLOFSOORTEN` (AD-27); het scherm loopt de
 * tabel af en schrijft de drie waarden nergens met de hand over (KE-9).
 */
import type { Verlofsoort } from '@functieloonhuis/shared';

/** §10.1 — het label van het keuzeveld zelf. */
export const VERLOFSOORT_VRAAG = 'Waar gaat het om?';

/** §10.1 — de melding bij het keuzeveld wanneer er niets gekozen is (AC-5). */
export const VERLOFSOORT_FOUT_GEEN_KEUZE = 'Kies waar het om gaat.';

/**
 * §10.1 — de zin bij het wisselen van soort (AC-6).
 *
 * Hij verschijnt ALLEEN wanneer er werkelijk iets is weggehaald. Vandaag is
 * dat nooit zo: het enige soort-eigen veld dat de PRD kent is de gebeurtenis
 * bij bijzonder verlof, en dat is story 10.1. De zin staat hier alvast zodat
 * die story hem niet alsnog hoeft te verzinnen.
 */
export const VERLOFSOORT_OPGERUIMD =
  'Je vulde gegevens in die bij de vorige keuze hoorden. Die zijn nu weggehaald.';

/**
 * §10.1 — per soort het label, de uitlegregel, en waar van toepassing de
 * wegwijsregel.
 *
 * De twee wegwijsregels zijn geen aankleding: zonder de regel bij *Bijzonder
 * verlof* is dit de enige plek waar iemand geboorteverlof zou indienen
 * (besluit V25), en zonder die bij *Calamiteiten- of zorgverlof* geldt
 * hetzelfde voor langdurend zorgverlof (besluit V18). Allebei zouden ze als
 * betaald verlof in de portal belanden.
 */
export const VERLOFSOORT_KEUZE_TEKSTEN: Record<
  Verlofsoort,
  { label: string; uitleg: string; wegwijs?: string }
> = {
  medisch_bezoek: {
    label: 'Medisch bezoek onder werktijd',
    uitleg:
      'Huisarts, specialist, fysiotherapeut, tandarts en dergelijke. Hiervoor geldt 3 uur per maand.',
  },
  bijzonder_verlof: {
    label: 'Bijzonder verlof',
    uitleg: 'Huwelijk, overlijden, jubileum of verhuizing. Dit beoordeelt HR.',
    wegwijs:
      'Gaat het om geboorteverlof na de bevalling van je partner? Neem dan contact op met HR — dat regel je niet hier.',
  },
  calamiteiten_of_zorgverlof: {
    label: 'Calamiteiten- of zorgverlof',
    uitleg:
      'Iets thuis dat meteen geregeld moet worden, of de zorg voor een ziek kind, partner of ouder. Dit beoordeelt HR — je leidinggevende hoeft er niet over te beslissen.',
    wegwijs:
      'Gaat het om langdurige zorg voor iemand die ernstig ziek is? Neem dan contact op met HR — dat regel je niet hier.',
  },
};

/**
 * §10.2 — wat het handboek over een soort zegt (AC-7).
 *
 * Een `Partial`, en dat is de hele afleiding: alleen medisch bezoek draagt
 * voorwaarden. Het scherm kijkt of er een blok in deze tabel staat en
 * vergelijkt nergens een soortnaam (KE-9), net zoals
 * `HR_BEOORDEELT_ZELF_TEKSTEN` dat doet.
 *
 * De resterende maandruimte staat in §10.2 in dezelfde tabel maar hoort hier
 * NIET bij: dat is story 6.2.
 */
export const VERLOFSOORT_VOORWAARDEN: Partial<
  Record<Verlofsoort, { kop: string; regels: string[] }>
> = {
  medisch_bezoek: {
    kop: 'Wat het handboek hierover zegt',
    regels: [
      'Alleen in noodzakelijke gevallen, na overleg met je leidinggevende of de directie. Je aanvraag hier goedkeuren ís dat overleg.',
      'Plan het bezoek zoveel mogelijk aan het begin of het einde van je dag.',
      'Werk je parttime, dan is de verwachting dat je deze bezoeken zoveel mogelijk op je werkdagen plant.',
    ],
  },
};
